import { motion } from 'motion/react';
import {
    Layers,
    Map as MapIcon,
    Satellite,
    Truck,
    Target,
    Cloud,
    CreditCard
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Separator } from '../ui/separator';

export interface MapLayerConfig {
    wells: boolean;
    fleet: boolean;
    competitors: boolean;
    weather: boolean;
    heatmap: boolean;
    satellite: boolean;
}

interface MapControlsProps {
    config: MapLayerConfig;
    onToggle: (key: keyof MapLayerConfig) => void;
    onFullscreen?: () => void;
}

export default function MapControls({ config, onToggle, onFullscreen }: MapControlsProps) {
    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 pointer-events-auto"
        >
            <div className="bg-slate-950/90 backdrop-blur-md rounded-full border border-slate-700 shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-1.5 flex items-center gap-1">

                {/* Layer Group: Core */}
                <div className="flex items-center gap-1 px-1">
                    <ControlButton
                        active={config.wells}
                        onClick={() => onToggle('wells')}
                        icon={Target}
                        label="Wells"
                        shortcut="W"
                        color="emerald"
                    />
                    <ControlButton
                        active={config.fleet}
                        onClick={() => onToggle('fleet')}
                        icon={Truck}
                        label="Fleet"
                        shortcut="F"
                        color="amber"
                    />
                    <ControlButton
                        active={config.competitors}
                        onClick={() => onToggle('competitors')}
                        icon={Layers}
                        label="Market"
                        shortcut="C"
                        color="blue"
                    />
                </div>

                <Separator orientation="vertical" className="h-6 bg-slate-700 mx-1" />

                {/* Layer Group: Intelligence */}
                <div className="flex items-center gap-1 px-1">
                    <ControlButton
                        active={config.weather}
                        onClick={() => onToggle('weather')}
                        icon={Cloud}
                        label="Wx Radar"
                        shortcut="R"
                        color="purple"
                    />
                    <ControlButton
                        active={config.heatmap}
                        onClick={() => onToggle('heatmap')}
                        icon={CreditCard}
                        label="Revenue"
                        shortcut="H"
                        color="green"
                    />
                </div>

                <Separator orientation="vertical" className="h-6 bg-slate-700 mx-1" />

                {/* View Mode */}
                <div className="flex items-center gap-1 px-1">
                    <ControlButton
                        active={config.satellite}
                        onClick={() => onToggle('satellite')}
                        icon={config.satellite ? Satellite : MapIcon}
                        label={config.satellite ? "Satellite" : "Map View"}
                        shortcut="S"
                        color="slate"
                    />
                </div>
            </div>
        </motion.div>
    );
}

// Helper Sub-Component
function ControlButton({
    active,
    onClick,
    icon: Icon,
    label,
    shortcut,
    color
}: {
    active: boolean;
    onClick: () => void;
    icon: any;
    label: string;
    shortcut?: string;
    color: string;
}) {
    // Dynamic color classes
    const getColorClasses = () => {
        switch (color) {
            case 'emerald': return active ? 'bg-emerald-600 text-white hover:bg-emerald-500' : 'bg-transparent text-slate-400 hover:bg-slate-800 hover:text-emerald-400';
            case 'amber': return active ? 'bg-amber-600 text-white hover:bg-amber-500' : 'bg-transparent text-slate-400 hover:bg-slate-800 hover:text-amber-400';
            case 'blue': return active ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-transparent text-slate-400 hover:bg-slate-800 hover:text-blue-400';
            case 'purple': return active ? 'bg-purple-600 text-white hover:bg-purple-500' : 'bg-transparent text-slate-400 hover:bg-slate-800 hover:text-purple-400';
            case 'green': return active ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-transparent text-slate-400 hover:bg-slate-800 hover:text-green-400';
            default: return active ? 'bg-slate-100 text-slate-900 hover:bg-white' : 'bg-transparent text-slate-400 hover:bg-slate-800 hover:text-white';
        }
    };

    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClick}
                        className={`h-9 px-3 rounded-full transition-all duration-200 border border-transparent ${active ? 'shadow-lg border-white/10' : ''} ${getColorClasses()}`}
                    >
                        <Icon className="h-4 w-4 mr-2" />
                        <span className="text-xs font-semibold">{label}</span>
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-slate-300 flex items-center gap-2">
                    <span>Toggle {label}</span>
                    {shortcut && <Badge variant="outline" className="text-[9px] h-4 bg-slate-800 border-slate-600 text-slate-400 px-1">{shortcut}</Badge>}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
